import { defineStore } from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { documentsApiConfig } from '@/config/api/documents';
import { useClientApi } from '@/plugins/api/lk';
import { usePortalApi } from '@/plugins/api/portal';
import { useSentry } from '@/plugins/sentry';
import { decorateApiActions } from '@/shared/lib';

import allCities from '../queries/allCities.graphql';

export interface GlobalCity {
  name: string;
  /** Название в предложном падеже */
  namePrepositional: string;
  slug: string;
  /** GlobalId с портального бэка */
  id: string;
}

interface InterfaceFile {
  aws: string;
}

interface ReglamentResponse {
  slug?: string;
  file: InterfaceFile;
  text: string;
}
interface Reglament {
  data: ReglamentResponse;
  isFetched: boolean;
}

export const useGlobalStore = defineStore('global', () => {
  const sentry = useSentry();
  const activeCity = ref<GlobalCity | null>(null);
  const citiesGlobal = ref<Array<GlobalCity>>([]);
  const reglament = ref<Reglament>({
    data: {
      file: {
        aws: '',
      },
      text: '',
    },
    isFetched: false,
  });

  const getReglamentLink = computed(() => reglament.value?.data?.file?.aws || '');
  const getReglamentText = computed(() => reglament.value?.data?.text || '');
  const getActiveCity = computed(() => activeCity.value);
  const getGlobalCities = computed((): Array<GlobalCity> => citiesGlobal.value);

  const setReglament = (payload: ReglamentResponse) => {
    reglament.value.data = payload;
    reglament.value.isFetched = true;
  };

  const getAllCities = async () => {
    const portalApi = usePortalApi();

    return portalApi.$post('/graphql/', {
      query: allCities.loc?.source.body,
    });
  };

  const getReglament = async () => {
    if (!reglament.value.isFetched) {
      const url = documentsApiConfig.detail('reglament');
      const response = await useClientApi().$get(url);
      setReglament(response);

      return response;
    }

    return reglament.value.data;
  };

  const globalInit = async () => {
    try {
      const [citiesRes] = await Promise.all([
        getAllCities(),
      ]);

      if (citiesRes.data?.currentCity) {
        activeCity.value = citiesRes.data.currentCity;
      }

      citiesGlobal.value = citiesRes?.data?.allCities?.edges?.length
        ? citiesRes.data.allCities.edges.map((item: { node: GlobalCity }) => item.node)
        : [];
    } catch (error) {
      console.log('🚀 ~ file: global.ts ~ globalInit ~ error', error);
      sentry.captureException(error);
      throw error;
    }
  };

  const decoratedApiActions = decorateApiActions({
    getAllCities,
    getReglament,
  }, 'store/global.ts', true);

  return {
    globalInit,
    activeCity,
    citiesGlobal,
    reglament,
    getReglamentLink,
    getReglamentText,
    getActiveCity,
    getGlobalCities,
    ...decoratedApiActions,
  };
});
